/** Same-origin notices contain no account, cookie, solution or other private data. */
const CHANNEL = 'ndrc.session-change';
const KEY = 'ndrc:session-change';
const sentHere = new Set<string>();
type Reason = 'login' | 'logout';
interface Notice {
  version: 1;
  id: string;
  reason: Reason;
}
function notice(value: unknown): value is Notice {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<Notice>;
  return (
    row.version === 1 &&
    typeof row.id === 'string' &&
    row.id.length > 0 &&
    (row.reason === 'login' || row.reason === 'logout')
  );
}
function stored(browser: Window): string | null {
  try {
    return browser.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}
export function announceSessionChange(reason: Reason): void {
  if (typeof window === 'undefined') return;
  const value: Notice = { version: 1, id: crypto.randomUUID(), reason };
  sentHere.add(value.id);
  if (sentHere.size > 32) sentHere.delete(sentHere.values().next().value!);
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* Channel remains available. */
  }
  try {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage(value);
    channel.close();
  } catch {
    /* Storage events and resume checks remain available. */
  }
}
/** Close already-rendered private screens, even when their network queries have stopped. */
export function installSessionBoundary(onChange: () => void): () => void {
  const browser = window;
  const page = document;
  const initial = stored(browser);
  let stopped = false;
  let channel: BroadcastChannel | undefined;
  function dispose() {
    stopped = true;
    browser.removeEventListener('storage', storageChanged);
    browser.removeEventListener('pageshow', resumed);
    page.removeEventListener('visibilitychange', resumed);
    channel?.removeEventListener('message', received);
    channel?.close();
  }
  function changed(value: unknown) {
    if (stopped || !notice(value) || sentHere.has(value.id)) return;
    dispose();
    onChange();
  }
  function received(event: MessageEvent) {
    changed(event.data);
  }
  function resumed() {
    const current = stored(browser);
    if (!current || current === initial) return;
    let value: unknown;
    try {
      value = JSON.parse(current);
    } catch {
      return; // Malformed storage is ignored, not a failure of the close callback.
    }
    changed(value);
  }
  function storageChanged(event: StorageEvent) {
    if (event.key === KEY) resumed();
  }
  browser.addEventListener('storage', storageChanged);
  browser.addEventListener('pageshow', resumed);
  page.addEventListener('visibilitychange', resumed);
  try {
    channel = new BroadcastChannel(CHANNEL);
    channel.addEventListener('message', received);
  } catch {
    /* Storage events still invalidate other tabs. */
  }
  return dispose;
}
